import React, { useCallback, useEffect, useRef, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import type { EmblaOptionsType } from 'embla-carousel'
import {
  Box,
  Button,
  ButtonVariant,
  ButtonColorVariant,
  ButtonSize,
  Display,
  DisplayPosition,
  JustifyContent,
  Overflow,
  SpaceScale,
  Space,
} from '@gousto-internal/citrus-react'
import { trackCarouselSlideChange } from '../../../tracking'

export type CarouselProps = {
  carouselAreaLabel: string
  children: React.ReactNode
  options?: EmblaOptionsType
  showNavButtons?: boolean
  autoHideNavButtons?: boolean
  startAtMiddleAvailableSnap?: boolean
  centerSlidesWhenNotScrollable?: boolean
  slidePaddingH?: SpaceScale
  slidePaddingV?: SpaceScale
  slideWidth?: string
  slideHeight?: string
  snowplowTrackingProperties?: Record<string, unknown>
}

export const Carousel = ({
  carouselAreaLabel,
  children,
  options,
  slidePaddingH,
  slidePaddingV,
  slideWidth,
  slideHeight,
  showNavButtons = true,
  autoHideNavButtons = false,
  startAtMiddleAvailableSnap = false,
  centerSlidesWhenNotScrollable = false,
  snowplowTrackingProperties,
}: CarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(options)
  const [canScrollPrev, setCanScrollPrev] = useState(true)
  const [canScrollNext, setCanScrollNext] = useState(true)
  const hasStartedAtMiddleAvailableSnap = useRef(false)
  const shouldCenterSlides = centerSlidesWhenNotScrollable && !canScrollPrev && !canScrollNext

  const onSettle = useCallback(
    (emblaApi) => {
      const newIndex = emblaApi.selectedScrollSnap()
      const prevIndex = emblaApi.previousScrollSnap()
      trackCarouselSlideChange(prevIndex, newIndex, snowplowTrackingProperties)
    },
    [snowplowTrackingProperties],
  )

  const onSelect = useCallback(() => {
    if (!emblaApi) {
      return
    }

    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
  }, [emblaApi])

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev()
    }
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext()
    }
  }, [emblaApi])

  const scrollToMiddleAvailableSnap = useCallback(() => {
    if (!emblaApi || !startAtMiddleAvailableSnap || hasStartedAtMiddleAvailableSnap.current) {
      return
    }

    const middleAvailableSnapIndex = Math.floor((emblaApi.scrollSnapList().length - 1) / 2)
    const currentSnapIndex = emblaApi.selectedScrollSnap()

    if (middleAvailableSnapIndex !== currentSnapIndex) {
      emblaApi.scrollTo(middleAvailableSnapIndex, true)
    }

    hasStartedAtMiddleAvailableSnap.current = true
  }, [emblaApi, startAtMiddleAvailableSnap])

  useEffect(() => {
    hasStartedAtMiddleAvailableSnap.current = false
  }, [emblaApi, startAtMiddleAvailableSnap])

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on('select', onSelect)
      emblaApi.on('reInit', onSelect)
      scrollToMiddleAvailableSnap()
      onSelect()
    }
    return () => {
      if (!emblaApi) {
        return
      }
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi, onSelect, scrollToMiddleAvailableSnap])

  useEffect(() => {
    if (!emblaApi) {
      return
    }

    emblaApi.on('settle', onSettle)

    return () => {
      emblaApi.off('settle', onSettle)
    }
  }, [emblaApi, onSettle])

  return (
    <Box
      className="embla"
      position={DisplayPosition.Relative}
      overflowX={Overflow.Hidden}
      overflowY={Overflow.Hidden}
    >
      <Box className="embla__viewport" ref={emblaRef}>
        <Box
          aria-label={carouselAreaLabel}
          aria-live="polite"
          aria-atomic="true"
          aria-roledescription="carousel"
          className="embla__container"
          display={Display.Flex}
          justifyContent={shouldCenterSlides ? JustifyContent.Center : undefined}
          role="region"
          tabIndex={0}
        >
          {React.Children.map(children, (child, index) => {
            return (
              <Box
                aria-label={`${index + 1} of ${React.Children.count(children)}`}
                aria-roledescription={`Slide ${index + 1} of ${React.Children.count(children)}`}
                className="embla__slide"
                data-testid="carousel-slide"
                flexBasis={slideWidth}
                flexGrow={0}
                flexShrink={0}
                height={slideHeight}
                key={index}
                paddingH={slidePaddingH}
                paddingV={slidePaddingV}
                role="group"
                tabIndex={-1}
              >
                {child}
              </Box>
            )
          })}
        </Box>
        {showNavButtons && (
          <Box
            aria-label="Carousel controls"
            display={Display.Flex}
            position={DisplayPosition.Absolute}
            role="group"
            top="45%"
            width="100%"
          >
            <Space direction="horizontal" size={4} />
            <Box justifyContent={JustifyContent.SpaceBetween} display={Display.Flex} width="100%">
              <Button
                aria-label="Previous slide"
                className="embla__prev"
                colorVariant={ButtonColorVariant.Secondary}
                icon="arrow_back"
                onClick={scrollPrev}
                size={ButtonSize.Large}
                style={{
                  borderRadius: '50%',
                  visibility: autoHideNavButtons && !canScrollPrev ? 'hidden' : 'visible',
                  pointerEvents: autoHideNavButtons && !canScrollPrev ? 'none' : 'auto',
                }}
                tabIndex={autoHideNavButtons && !canScrollPrev ? -1 : 0}
                aria-hidden={autoHideNavButtons && !canScrollPrev}
                variant={ButtonVariant.Icon}
              />

              <Button
                aria-label="Next slide"
                className="embla__next"
                colorVariant={ButtonColorVariant.Secondary}
                icon="arrow_right"
                onClick={scrollNext}
                size={ButtonSize.Large}
                style={{
                  borderRadius: '50%',
                  visibility: autoHideNavButtons && !canScrollNext ? 'hidden' : 'visible',
                  pointerEvents: autoHideNavButtons && !canScrollNext ? 'none' : 'auto',
                }}
                tabIndex={autoHideNavButtons && !canScrollNext ? -1 : 0}
                aria-hidden={autoHideNavButtons && !canScrollNext}
                variant={ButtonVariant.Icon}
              />
            </Box>
            <Space direction="horizontal" size={4} />
          </Box>
        )}
      </Box>
    </Box>
  )
}
